import {ICompareValueItem, IConditionFieldPreset} from "../../../policy-types";
import type {ILocationPersistenceToken, ILocationToken} from "./location-selection-types";
import {
    ISemanticGroup,
    isSemanticGroupCountryOnlyMember,
    partitionFullSemanticGroupSelections,
} from "./location-semantic-group-collapse";

export type {ISemanticGroup} from "./location-semantic-group-collapse";

/**
 * LocationSemanticGroupConverter (stateless utility)
 * - Discovers semantic groups (e.g. EU countries) declared by the location field preset
 * - Hydrates persistence tokens with group context and collapses full member sets for the UI
 * - Expands group tokens back to member country tokens for persistence
 */
class LocationSemanticGroupConverter {

    private readonly _semanticGroups: ISemanticGroup[];
    private readonly _semanticGroupCodes: Set<string>;
    private readonly _groupLabels: Map<string, string>;

    constructor(fieldPreset: IConditionFieldPreset) {
        this._semanticGroups = this._buildSemanticGroups(
            fieldPreset.compare_value_data as ICompareValueItem[],
            fieldPreset.country_group_codes ?? []
        );
        this._semanticGroupCodes = new Set(this._semanticGroups.map((group) => group.code));
        this._groupLabels = new Map(this._semanticGroups.map((group) => [group.code, group.label]));
    }

    /** Returns the semantic groups declared by the field preset. */
    public getSemanticGroups(): ISemanticGroup[] {
        return this._semanticGroups;
    }

    /** Returns true when the code is a semantic group node, not a real country. */
    public isSemanticGroupCode(code: string): boolean {
        return this._semanticGroupCodes.has(code);
    }

    /** Returns semantic group label by code. O(1) lookup. Falls back to code if not found. */
    public getGroupName(groupCode: string): string {
        return this._groupLabels.get(groupCode) || groupCode;
    }

    /**
     * Validates the group context of a raw token, before conversions that would drop or rewrite it.
     * A token may omit the group, or declare a known group that the country actually belongs to.
     */
    public isTokenGroupContextValid(token: ILocationToken): boolean {
        if (!token.group) {
            return true;
        }

        const group = this._semanticGroups.find((item) => item.code === token.group);

        if (!group) {
            return false;
        }

        return !token.country || group.memberCodes.includes(token.country);
    }

    /** Compares token lists by persistence shape (order-independent). */
    public areTokenListsEqual(
        left: ILocationToken[] | undefined,
        right: ILocationToken[] | undefined
    ): boolean {
        if (left === undefined && right === undefined) {
            return true;
        }

        if (left === undefined || right === undefined) {
            return false;
        }

        const leftTokenSet = new Set(
            this.expandTokensForPersistence(left).map((token) => JSON.stringify(token))
        );
        const rightTokenSet = new Set(
            this.expandTokensForPersistence(right).map((token) => JSON.stringify(token))
        );

        if (leftTokenSet.size !== rightTokenSet.size) {
            return false;
        }

        for (const key of leftTokenSet) {
            if (!rightTokenSet.has(key)) {
                return false;
            }
        }

        return true;
    }

    /**
     * Ensures every token has an explicit group context for UI state
     * and collapses fully selected member sets into a single group token.
     */
    public hydrateTokensForUi(tokens: ILocationToken[]): ILocationToken[] {
        const hydratedTokens = tokens.map((token) => this._hydrateTokenGroup(token));
        const {fullGroupKeys, remainingTokens} = partitionFullSemanticGroupSelections(
            hydratedTokens,
            this._semanticGroups,
            {
                resolveGroupKey: (group) => group.code,
                isCountryOnlyMember: isSemanticGroupCountryOnlyMember,
            }
        );

        return [
            ...fullGroupKeys.map((groupKey) => ({group: groupKey})),
            ...remainingTokens,
        ];
    }

    /**
     * Expands group tokens to member country codes for persistence.
     * Group codes (e.g. EU) must never be stored in compare_value.
     */
    public expandTokensForPersistence(tokens: ILocationToken[]): ILocationPersistenceToken[] {
        const expanded: ILocationPersistenceToken[] = [];
        const seenTokenKeys = new Set<string>();

        /** Group expansion can restate a country already listed on its own, so persisted tokens stay unique. */
        const addUniqueToken = (token: ILocationPersistenceToken): void => {
            const tokenKey = `${token.country}|${token.province ?? ''}|${token.city ?? ''}`;

            if (seenTokenKeys.has(tokenKey)) {
                return;
            }

            seenTokenKeys.add(tokenKey);
            expanded.push(token);
        };

        for (const token of tokens) {
            if (token.group && !token.country) {
                const group = this._semanticGroups.find((item) => item.code === token.group);
                group?.memberCodes.forEach((memberCode) => addUniqueToken({country: memberCode}));
                continue;
            }

            if (!token.country) {
                continue;
            }

            addUniqueToken({
                country: token.country,
                ...(token.province ? {province: token.province} : {}),
                ...(token.city ? {city: token.city} : {}),
            });
        }

        return expanded;
    }

    /** Builds semantic groups from the preset dataset, keyed by the codes the API marks as groups. */
    private _buildSemanticGroups(
        dataset: ICompareValueItem[],
        semanticGroupCodes: string[]
    ): ISemanticGroup[] {
        if (!semanticGroupCodes.length) {
            return [];
        }

        const groupCodeSet = new Set(semanticGroupCodes);

        return dataset
            .filter((item) => item.code && groupCodeSet.has(item.code))
            .map((item) => ({
                code: item.code!,
                label: item.name || item.code!,
                memberCodes: (item.countries ?? [])
                    .map((country) => country.code)
                    .filter((code): code is string => Boolean(code)),
            }));
    }

    /**
     * Derives the group of a country token from membership, which is the single source of truth.
     * Group context arriving on the token is not trusted: a stale or mismatched group is repaired here,
     * while saving such a token is rejected by validation.
     */
    private _hydrateTokenGroup(token: ILocationToken): ILocationToken {
        // shouldn't happen, a safe guard, because type definition has it as optional
        if (!token.country) {
            return {group: token.group ?? null};
        }

        const memberGroup = this._findSemanticGroupForMemberCountry(token.country);

        return {
            group: memberGroup?.code ?? null,
            country: token.country,
            ...(token.province ? {province: token.province} : {}),
            ...(token.city ? {city: token.city} : {}),
        };
    }

    /** Returns the semantic group that contains the given member country code, if any. */
    private _findSemanticGroupForMemberCountry(countryCode: string): ISemanticGroup | undefined {
        return this._semanticGroups.find((group) => group.memberCodes.includes(countryCode));
    }
}

export default LocationSemanticGroupConverter;
